import React, {
  cloneElement,
  type MouseEvent,
  useMemo,
  useRef,
  useState,
} from 'react';

import Dropdown from '@/components/Dropdown/Dropdown';
import useColor from '@/hooks/domain/useColor';
import useCombinedRefs from '@/hooks/pure/useCombinedRefs';

import { Divider, Flex } from '../base';
import { ScrollArea } from '../shadcn/ui/scroll-area';
import Typo from '../Typography/Typo';
import MenuItem from './MenuItem';
import { type MenuItemProps, MenuItemType, type MenuProps } from './types';

const Menu = (props: MenuProps) => {
  const color = useColor();
  const ref = useRef<HTMLDivElement>(null);
  const combinedRef = useCombinedRefs(ref, props.anchorRef);
  const [modalOpen, setModalOpen] = useState(false);
  // 현재 열린 subMenu의 ID를 추적
  const [openSubMenuId, setOpenSubMenuId] = useState<string | null>(null);

  const clonedAnchor = useMemo(() => {
    return cloneElement(props.triggerNode, {
      ref: combinedRef,
      onClick: (e: MouseEvent) => {
        e.stopPropagation();
        setModalOpen(true);
        props.openMenu?.();
        props.onAnchorClickAction?.(e);
      },
    });
  }, [props.triggerNode, props.openMenu, props.onAnchorClickAction]);

  const onCloseClick = () => {
    setModalOpen(false);
    setOpenSubMenuId(null); // 메뉴가 닫힐 때 subMenu도 모두 닫기
    props.closeMenu?.();
  };

  const onItemClick = (e: MouseEvent, option: MenuItemProps) => {
    e.stopPropagation();
    option.onClick?.(e);
    setModalOpen(false);
    setOpenSubMenuId(null); // 메뉴 아이템 클릭 시 subMenu도 닫기
  };

  const modalOpenStatus = useMemo(() => {
    if (typeof props.isOpen === 'boolean') {
      return props.isOpen;
    }
    return modalOpen;
  }, [props.isOpen, modalOpen]);

  return (
    <React.Fragment>
      {clonedAnchor}
      <Dropdown
        triggerEl={combinedRef.current}
        modalWidth={props.modalWidth || '150px'}
        status={modalOpenStatus}
        close={onCloseClick}
        modalOptions={
          props.modalOptions || {
            offset: {
              y: 8,
            },
          }
        }
        style={{
          padding: 4,
          overflow: 'auto',
          ...props.menuStyle,
        }}
      >
        <ScrollArea>
          {props.options
            .filter((option) => !option.hidden)
            .map((option, index) => {
              if (option.type === MenuItemType.SECTION_TITLE) {
                return (
                  <Flex padding="6px 8px" key={`${option.label}-${index}`}>
                    <Typo type="label3" color={color.gray600}>
                      {option.label}
                    </Typo>
                  </Flex>
                );
              }
              if (option.type === MenuItemType.DIVIDER)
                return (
                  <Flex padding="4px 8px" key={`${option.label}-${index}`}>
                    <Divider />
                  </Flex>
                );
              return (
                <MenuItem
                  className={option.className}
                  key={option.label}
                  id={option.label}
                  icon={option.icon}
                  disabled={option.disabled}
                  tooltip={option.tooltip}
                  onClick={(e) => onItemClick(e, option)}
                  node={option.label}
                  subMenus={option.subMenus}
                  subMenuWidth={option.subMenuWidth}
                  subMenuOptions={
                    option.subMenus
                      ? {
                          // 외부에서 subMenuOptions를 제공하지 않은 경우에만 내부 상태 사용
                          isOpen:
                            option.subMenuOptions?.isOpen ??
                            openSubMenuId === option.label,
                          openMenu: () => {
                            // 다른 subMenu가 열려있으면 닫기
                            if (
                              openSubMenuId &&
                              openSubMenuId !== option.label
                            ) {
                              setOpenSubMenuId(null);
                            }
                            setOpenSubMenuId(option.label);
                            option.subMenuOptions?.openMenu?.();
                          },
                          closeMenu: () => {
                            setOpenSubMenuId(null);
                            option.subMenuOptions?.closeMenu?.();
                          },
                        }
                      : option.subMenuOptions
                  }
                  closeMenu={onCloseClick}
                />
              );
            })}
        </ScrollArea>
      </Dropdown>
    </React.Fragment>
  );
};

export default Menu;
